import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Hidden, Drawer, Grid, Dialog, Button, Typography, IconButton } from '@material-ui/core';
import Navigator from './Navigator';
// import Header from './NewHeader';
import BottomNavigator from './BottomNavigator';
import {
    SIDEBAR_LEFT_CONTENT,
    SIDEBAR_LEFT_WIDTH,
    ROOMS,
    TOPICS,
    TOKEN_NAME,
    MerchantRealtimeActionTypes
} from 'const';
// import MenuIcon from '@material-ui/icons/Menu';
import { useIsOpenSidebarLeft, useOpenLogoutPopup } from 'hooks/app';
import { useDispatch } from 'react-redux';
import * as appActions from 'app/actions';
import { io } from 'socket.io-client';
import { getItem } from 'utils/localStorage';
import { API_URL } from '../config';
import { clearToken } from 'utils/token';
import { useUserMerchantId } from 'hooks/auth';
import DialogActions from 'components/dialog/Action';
import DialogContent from 'components/dialog/Content';
import DialogTitle from 'components/dialog/Title';
import { useHistory, useLocation } from 'react-router-dom';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import CloseIcon from '@material-ui/icons/Close';
import { useMerchantSubscriptionStatusQuery } from 'services/payment';

const VALID_SUBSCRIPTION_STATUSES = ['active', 'trialing'];
const SUBSCRIPTION_REMINDER_DAYS = 14;

function shouldShowSubscriptionReminder(status) {
    if (!status?.requireSubscription) return false;
    if (VALID_SUBSCRIPTION_STATUSES.includes(status?.subscription?.status)) return false;

    if (!status?.subscriptionStartAt) return true;
    const startAt = new Date(status.subscriptionStartAt);
    if (Number.isNaN(startAt.getTime())) return true;

    const daysUntilStart = (startAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24);
    return daysUntilStart <= SUBSCRIPTION_REMINDER_DAYS;
}

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        background: theme.colors.background,
        position: 'relative',
        height: '100vh',
        width: '100vw',
        overflow: 'hidden',
        maxHeight: `-webkit-fill-available`
    },
    contentOpen: {
        marginLeft: SIDEBAR_LEFT_WIDTH + SIDEBAR_LEFT_CONTENT,
        width: `calc(100vw - ${SIDEBAR_LEFT_WIDTH + SIDEBAR_LEFT_CONTENT}px)`,
        [theme.breakpoints.down('sm')]: {
            marginLeft: SIDEBAR_LEFT_CONTENT,
            width: `calc(100vw - ${SIDEBAR_LEFT_CONTENT}px)`
        },
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen
        })
    },
    contentClose: {
        width: `calc(100vw - ${SIDEBAR_LEFT_WIDTH}px)`,
        marginLeft: SIDEBAR_LEFT_WIDTH,
        [theme.breakpoints.down('sm')]: {
            width: `calc(100vw)`,
            marginLeft: 0
        },
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen
        })
    },
    sidebarContent: {
        borderLeft: `1px solid ${theme.colors.divider}`,
        flex: 1
    },
    drawer: {
        zIndex: 99,
        top: 0
    },
    drawerOpen: {
        width: SIDEBAR_LEFT_WIDTH + SIDEBAR_LEFT_CONTENT,
        [theme.breakpoints.down('sm')]: {
            width: SIDEBAR_LEFT_CONTENT
        },
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen
        })
    },
    drawerClose: {
        width: SIDEBAR_LEFT_WIDTH,
        [theme.breakpoints.down('sm')]: {
            width: 0
        },
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen
        })
    },
    appBar: {
        left: SIDEBAR_LEFT_WIDTH,
        [theme.breakpoints.down('sm')]: {
            left: 0
        },
        zIndex: theme.zIndex.drawer + 1,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen
        })
    },
    appBarShift: {
        marginLeft: SIDEBAR_LEFT_CONTENT,
        width: `calc(100% - ${SIDEBAR_LEFT_WIDTH + SIDEBAR_LEFT_CONTENT}px)`,
        [theme.breakpoints.down('sm')]: {
            marginLeft: 0,
            width: `calc(100% - ${SIDEBAR_LEFT_CONTENT}px)`
        },
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen
        })
    },
    menuButton: {
        marginRight: 36
    },
    hide: {
        display: 'none'
    },
    footer: {
        position: 'fixed',
        bottom: 0,
        left: 0,
        width: '100vw',
        zIndex: theme.zIndex.drawer + 2
    },
    paymentEmailPaper: {
        width: 640,
        maxWidth: 'calc(100vw - 32px)',
        borderRadius: 18,
        boxShadow: '0px 18px 40px rgba(15, 23, 42, 0.22)',
        overflow: 'visible'
    },
    paymentEmailContent: {
        position: 'relative',
        display: 'flex',
        padding: theme.spacing(5),
        paddingRight: theme.spacing(4),
        [theme.breakpoints.down('xs')]: {
            padding: theme.spacing(3),
            paddingRight: theme.spacing(2.5)
        }
    },
    paymentEmailIconWrap: {
        width: 64,
        height: 64,
        minWidth: 64,
        borderRadius: '50%',
        background: '#EAF4FF',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.palette.primary.main,
        marginRight: theme.spacing(3),
        '& svg': {
            width: 34,
            height: 34
        },
        [theme.breakpoints.down('xs')]: {
            width: 48,
            height: 48,
            minWidth: 48,
            marginRight: theme.spacing(2),
            '& svg': {
                width: 28,
                height: 28
            }
        }
    },
    paymentEmailBody: {
        flex: 1,
        paddingRight: theme.spacing(2)
    },
    paymentEmailTitle: {
        color: '#0B1220',
        fontSize: 28,
        lineHeight: '34px',
        fontWeight: 700,
        marginBottom: theme.spacing(2.5),
        [theme.breakpoints.down('xs')]: {
            fontSize: 22,
            lineHeight: '28px'
        }
    },
    paymentEmailText: {
        color: '#374151',
        fontSize: 22,
        lineHeight: '34px',
        marginBottom: theme.spacing(2.5),
        maxWidth: 430,
        [theme.breakpoints.down('xs')]: {
            fontSize: 16,
            lineHeight: '24px'
        }
    },
    paymentEmailClose: {
        position: 'absolute',
        top: 28,
        right: 28,
        color: '#8A8F98',
        [theme.breakpoints.down('xs')]: {
            top: 14,
            right: 14
        }
    },
    paymentEmailActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: theme.spacing(4)
    },
    paymentEmailButton: {
        minWidth: 138,
        height: 64,
        borderRadius: 10,
        fontSize: 20,
        fontWeight: 700,
        textTransform: 'none',
        [theme.breakpoints.down('xs')]: {
            minWidth: 110,
            height: 48,
            fontSize: 16
        }
    }
}));

function MainLayout({ children }) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const isOpenSidebarLeft = useIsOpenSidebarLeft();
    const merchantId = useUserMerchantId();
    const openLogoutPopup = useOpenLogoutPopup();
    const history = useHistory();
    const location = useLocation();
    const [openPaymentEmailPopup, setOpenPaymentEmailPopup] = React.useState(false);
    const isSubscriptionRoute = location.pathname.indexOf('/subscription') === 0;

    const token = getItem(TOKEN_NAME);

    useMerchantSubscriptionStatusQuery({
        enabled: !!merchantId && !isSubscriptionRoute,
        onSuccess: (status) => {
            if (shouldShowSubscriptionReminder(status)) {
                setOpenPaymentEmailPopup(true);
            }
        }
    });

    const handleRealtime = React.useCallback(
        (body) => {
            if (!body) return;
            switch (body?.action) {
                case MerchantRealtimeActionTypes.Logout: {
                    clearToken();
                    dispatch(appActions.setOpenLogoutPopup(true));
                    break;
                }
                case MerchantRealtimeActionTypes.PaymentEmailReceived: {
                    if (!isSubscriptionRoute) {
                        setOpenPaymentEmailPopup(true);
                    }
                    break;
                }
                default:
                    return;
            }
        },
        [dispatch, isSubscriptionRoute]
    );

    const handleRedirectLogin = React.useCallback(() => {
        dispatch(appActions.setOpenLogoutPopup(false));
        history.push('/login');
    }, [dispatch, history]);

    const handleClosePaymentEmailPopup = React.useCallback(() => {
        setOpenPaymentEmailPopup(false);
    }, []);

    const handleOpenSubscription = React.useCallback(() => {
        setOpenPaymentEmailPopup(false);
        history.push('/subscription');
    }, [history]);

    React.useEffect(() => {
        if (isSubscriptionRoute) {
            setOpenPaymentEmailPopup(false);
        }
    }, [isSubscriptionRoute]);

    React.useEffect(() => {
        const bookingSocket = io(`${API_URL}/app`, {
            transports: ['websocket'],
            autoConnect: true,
            reconnectionDelay: 2000,
            query: {
                token
            }
        });
        bookingSocket.on('connect', () => {
            bookingSocket.emit(ROOMS.JOIN_ROOM, TOPICS.merchant(merchantId));
        });

        bookingSocket.on(ROOMS.JOINED_ROOM, (room) => {
            console.log('JOINED_ROOM', room);
        });

        bookingSocket.on(ROOMS.MODIFIED, (body) => {
            handleRealtime(body);
        });

        bookingSocket.on(ROOMS.LEAVED_ROOM, (body) => {
            console.log('data LEAVED_ROOM', body);
        });

        return () => {
            bookingSocket.emit(ROOMS.LEAVE_ROOM, TOPICS.merchant(merchantId));
        };
    }, [merchantId, token, handleRealtime]);

    const handleDrawerToggle = () => {
        dispatch(appActions.toggleDrawlerLeft());
    };

    return (
        <div className={classes.root}>
            <Hidden>
                <Drawer
                    anchor="left"
                    variant="permanent"
                    classes={{
                        paper: `${classes.drawer} ${isOpenSidebarLeft ? classes.drawerOpen : classes.drawerClose}`
                    }}
                >
                    <Grid container wrap="nowrap" className={classes.fullHeight}>
                        <Navigator open={isOpenSidebarLeft} onClose={handleDrawerToggle} />
                    </Grid>
                </Drawer>
            </Hidden>

            <Grid item className={`${isOpenSidebarLeft ? classes.contentOpen : classes.contentClose}`}>
                {children}
            </Grid>

            <Hidden smUp implementation="js">
                <footer className={classes.footer}>
                    <BottomNavigator onDrawerToggle={handleDrawerToggle} />
                </footer>
            </Hidden>
            <Dialog maxWidth={false} open={openLogoutPopup}>
                <DialogTitle id="alert-dialog-title">
                    <Typography variant="h5">Logout app</Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Your merchant admin has changed your password, please login again.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        fullWidth
                        onClick={handleRedirectLogin}
                        style={{ height: 46 }}
                        color="primary"
                        variant="contained"
                    >
                        Agree
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                maxWidth={false}
                open={!isSubscriptionRoute && openPaymentEmailPopup}
                onClose={handleClosePaymentEmailPopup}
                classes={{ paper: classes.paymentEmailPaper }}
                BackdropProps={{ style: { backgroundColor: 'rgba(36, 43, 48, 0.38)' } }}
                aria-labelledby="payment-required-title"
            >
                <div className={classes.paymentEmailContent}>
                    <div className={classes.paymentEmailIconWrap}>
                        <InfoOutlinedIcon />
                    </div>
                    <div className={classes.paymentEmailBody}>
                        <Typography id="payment-required-title" className={classes.paymentEmailTitle}>
                            Payment Required
                        </Typography>
                        <Typography className={classes.paymentEmailText}>
                            Your merchant subscription needs a card on file.
                        </Typography>
                        <Typography className={classes.paymentEmailText}>
                            Review your monthly and yearly pricing, then add a card to keep your subscription active.
                        </Typography>
                        <div className={classes.paymentEmailActions}>
                            <Button
                                className={classes.paymentEmailButton}
                                onClick={handleOpenSubscription}
                                variant="contained"
                                color="primary"
                            >
                                Add Card
                            </Button>
                        </div>
                    </div>
                    <IconButton
                        className={classes.paymentEmailClose}
                        aria-label="Close"
                        onClick={handleClosePaymentEmailPopup}
                    >
                        <CloseIcon />
                    </IconButton>
                </div>
            </Dialog>
        </div>
    );
}

export default React.memo(MainLayout);
